import { BASE_LOGIN_CANVAS_HEIGHT, BASE_LOGIN_FRAME_WIDTH } from './layout';

const DESKTOP_PWA_WINDOW_SIZE_KEY = 'grato-desktop-pwa-window-size-v1';
const MIN_DESKTOP_SCREEN_WIDTH = 900;
const MIN_DESKTOP_SCREEN_HEIGHT = 700;
const RESIZE_THRESHOLD_PX = 24;
const FALLBACK_WINDOW_FRAME_WIDTH = 32;
const FALLBACK_WINDOW_FRAME_HEIGHT = 88;

function clamp(value: number, minValue: number, maxValue: number): number {
  return Math.min(maxValue, Math.max(minValue, value));
}

function isDesktopStandalonePwa(): boolean {
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
  const hasDesktopPointer = window.matchMedia(
    '(hover: hover) and (pointer: fine)',
  ).matches;
  const hasDesktopScreen =
    Math.floor(window.screen.availWidth) >= MIN_DESKTOP_SCREEN_WIDTH &&
    Math.floor(window.screen.availHeight) >= MIN_DESKTOP_SCREEN_HEIGHT;

  return isStandalone && hasDesktopPointer && hasDesktopScreen;
}

function hasAppliedDesktopWindowSize(): boolean {
  try {
    return window.localStorage.getItem(DESKTOP_PWA_WINDOW_SIZE_KEY) === '1';
  } catch {
    return false;
  }
}

function markDesktopWindowSizeAsApplied(): void {
  try {
    window.localStorage.setItem(DESKTOP_PWA_WINDOW_SIZE_KEY, '1');
  } catch {
    // Ignore storage failures and keep runtime behavior safe.
  }
}

function resolveTargetOuterSize(
  targetInnerSize: number,
  currentOuterSize: number,
  currentInnerSize: number,
  fallbackFrameSize: number,
  maxAvailableSize: number,
): number {
  const measuredFrameSize = Math.max(
    0,
    Math.round(currentOuterSize - currentInnerSize),
  );
  const frameSize = measuredFrameSize || fallbackFrameSize;

  return clamp(
    targetInnerSize + frameSize,
    targetInnerSize,
    Math.floor(maxAvailableSize),
  );
}

function shouldResizeWindow(
  targetOuterWidth: number,
  targetOuterHeight: number,
): boolean {
  return (
    Math.abs(window.outerWidth - targetOuterWidth) > RESIZE_THRESHOLD_PX ||
    Math.abs(window.outerHeight - targetOuterHeight) > RESIZE_THRESHOLD_PX
  );
}

export function fitDesktopPwaWindow(): void {
  if (typeof window === 'undefined') {
    return;
  }

  if (!isDesktopStandalonePwa() || hasAppliedDesktopWindowSize()) {
    return;
  }

  const targetOuterWidth = resolveTargetOuterSize(
    BASE_LOGIN_FRAME_WIDTH,
    window.outerWidth,
    window.innerWidth,
    FALLBACK_WINDOW_FRAME_WIDTH,
    window.screen.availWidth,
  );

  const targetOuterHeight = resolveTargetOuterSize(
    BASE_LOGIN_CANVAS_HEIGHT,
    window.outerHeight,
    window.innerHeight,
    FALLBACK_WINDOW_FRAME_HEIGHT,
    window.screen.availHeight,
  );

  const centeredLeft = Math.max(
    0,
    Math.round((window.screen.availWidth - targetOuterWidth) / 2),
  );
  const centeredTop = Math.max(
    0,
    Math.round((window.screen.availHeight - targetOuterHeight) / 2),
  );

  try {
    if (shouldResizeWindow(targetOuterWidth, targetOuterHeight)) {
      window.resizeTo(targetOuterWidth, targetOuterHeight);
    }

    window.moveTo(centeredLeft, centeredTop);
    markDesktopWindowSizeAsApplied();
  } catch {
    // Ignore platform limitations: some runtimes can block resize/move APIs.
  }
}
